import { Avatar, Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Card from '/@/components/bayer/card';
import Icon from '/@/components/icon';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useCropProtectionOptions } from '/@/hooks/crop-protection';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { MuiSelect } from '/@/mui/select';
import type { CropProtectionForm } from '/@/types/crop-protection';
import { generateUniques } from '/@/utils/crop-protection';
import useIsMobile from '/@/utils/hooks/is-mobile';
import WeedModal from '../weed-modal';

const isWeedSpecificationType = (type: CropProtectionForm['type']) => type === 'weeds';

const SpecificationsStep = ({ form }: { form: UseFormReturn<CropProtectionForm> }) => {
    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const formType = form.watch('type');
    const weedType = form.watch('weedType');
    const plagueId = form.watch('plagueId');
    const diseaseId = form.watch('diseaseId');
    const weedSize = form.watch('weedSize');

    useEffect(() => {
        form.reset({
            ...form.getValues(),
            recommendationOption: '',
            diseaseId: '',
            applicationType: '',
            plagueId: '',
        });
    }, [form]);
    const isMobile = useIsMobile();
    const hasSymptomChoices = [
        { label: _('Si, tiene síntomas'), value: 'healing' },
        { label: _('No, es a modo preventivo'), value: 'preventive' },
    ];

    const { data: filteredOptions, isValidating: areWeedsLoading } = useCropProtectionOptions({
        crop: form.getValues().crop,
        applicationMoment: form.getValues().application,
        type: form.getValues().type,
    });
    const { data: filteredWeedOptions, isValidating: isLoading } = useCropProtectionOptions({
        crop: form.getValues().crop,
        applicationMoment: form.getValues().application,
        weedType: form.getValues().weedType,
        type: form.getValues().type,
    });
    const { data: filteredSizeOptions, isValidating: areSizesLoading } = useCropProtectionOptions({
        crop: form.getValues().crop,
        applicationMoment: form.getValues().application,
        weedType: form.getValues().weedType,
        weed: form.getValues().weed,
        type: form.getValues().type,
    });

    const weedOptions = generateUniques('weed', filteredWeedOptions);
    const selectedWeed = weedOptions?.find((w) => {
        return w.value === form.getValues().weed;
    });

    const titleMapping = {
        weeds: _('Tipo de maleza'),
        plagues: _('Tipo de plaga'),
        diseases: _('Tipo de enfermedad'),
        seed_treatment: _('Tipo de maleza'),
    };

    const subtitleMapping = {
        weeds: _('Indicá la familia, el nombre y el tamaño de la maleza que deseas controlar.'),
        plagues: _('Indicá el nombre de la plaga que deseas controlar.'),
        diseases: _('Indicá el nombre de la enfermedad que deseas tratar.'),
        seed_treatment: _('Indicá la familia, el nombre y el tamaño de la maleza que deseas controlar.'),
    };

    const selectLabelMapping = {
        weeds: _('Tipo'),
        plagues: _('Nombre Plaga'),
        diseases: _('Nombre Enfermedad'),
        seed_treatment: _('Tipo'),
    };

    const selectOnChangeMapping = {
        weeds: (e) => {
            form.reset({
                ...form.getValues(),
                weedType: e.target.value,
                weed: '',
                weedSize: '',
            });
        },
        plagues: (e) => {
            form.reset({
                ...form.getValues(),
                plagueId: e.target.value,
            });
        },
        diseases: (e) => {
            form.reset({
                ...form.getValues(),
                diseaseId: e.target.value,
            });
        },
        seed_treatment: (e) => {
            form.reset({
                ...form.getValues(),
                weedType: e.target.value,
                weed: '',
                weedSize: '',
            });
        },
    };

    const selectOptionsMapping = {
        weeds: generateUniques('weedType', filteredOptions).map((option) => ({
            ...option,
            label: <Typography color='primary'>{option.label}</Typography>,
        })),
        plagues: generateUniques('plague', filteredOptions).map((option) => ({
            ...option,
            label: <Typography color='primary'>{option.label}</Typography>,
        })),
        seed_treatment: generateUniques('weedType', filteredOptions).map((option) => ({
            ...option,
            label: <Typography color='primary'>{option.label}</Typography>,
        })),
        diseases: generateUniques('disease', filteredOptions).map((option) => ({
            ...option,
            label: <Typography color='primary'>{option.label}</Typography>,
        })),
        weedSize: generateUniques('weedSize', filteredSizeOptions).map((option) => ({
            ...option,
            label: <Typography color='primary'>{option.label}</Typography>,
        })),
    };

    return (
        <>
            <Stack spacing={4}>
                <Stack spacing={1}>
                    <Stack>
                        <Typography color='primary' variant='body1' sx={{ fontWeight: 700 }}>
                            {titleMapping[formType] || _('Tipo de maleza')}
                        </Typography>
                    </Stack>
                    <Stack>
                        <Typography color='primary.mui.inactive' variant='body2'>
                            {subtitleMapping[formType] ||
                                _('Indicá la familia, el nombre y el tamaño de la maleza que deseas controlar.')}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
                    <Stack width={isMobile ? '100%' : '33%'} minWidth={'fit-content'}>
                        <MuiSelect
                            formControlProps={{
                                sx: { width: { xs: '100%', md: '-webkit-fill-available' } },
                            }}
                            inputLabel={<Typography color='primary'>{selectLabelMapping[formType]}</Typography>}
                            placeholder={selectLabelMapping[formType]}
                            value={isWeedSpecificationType(formType) ? weedType : plagueId || diseaseId || ''}
                            onChange={selectOnChangeMapping[formType]}
                            options={selectOptionsMapping[formType]}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                        />
                    </Stack>
                    {weedOptions.length > 0 && isWeedSpecificationType(formType) ? (
                        <Stack width={isMobile ? '100%' : '33%'}>
                            <MuiAutocomplete
                                renderOption={(props, option) => (
                                    <Box
                                        component='li'
                                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                                        {...props}
                                    >
                                        <Avatar
                                            src={option.images?.[0]?.file || option.label}
                                            alt={option.label}
                                            sx={{ width: 35, height: 35 }}
                                        />
                                        <Box>
                                            <Typography variant='body1' color='primary'>
                                                {option.label}
                                            </Typography>
                                            <Typography variant='body2' color='primary.mui.inactive'>
                                                {option.scientificName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                disableClearable
                                sx={{
                                    width: { xs: '100%', md: '-webkit-fill-available' },
                                    '& .MuiInputBase-input': {
                                        color: (t) => t.palette.primary.main,
                                    },
                                }}
                                label={
                                    <Typography
                                        color={
                                            areWeedsLoading || !form.getValues().weedType
                                                ? 'primary.mui.inactive'
                                                : 'primary'
                                        }
                                    >
                                        {_('Maleza')}
                                    </Typography>
                                }
                                disabled={areWeedsLoading || !form.getValues().weedType}
                                value={form.getValues().weed}
                                onChange={(_, option) => {
                                    form.reset({
                                        ...form.getValues(),
                                        weed: option.value,
                                        weedSize: '',
                                    });
                                }}
                                options={weedOptions}
                                loading={isLoading}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                noOptionsText={<Typography color='primary'>{_('Sin opciones')}</Typography>}
                            />
                        </Stack>
                    ) : null}
                    {!!filteredSizeOptions?.length &&
                        isWeedSpecificationType(formType) &&
                        selectOptionsMapping.weedSize.length > 0 && (
                            <Stack width={isMobile ? '100%' : '33%'}>
                                <MuiSelect
                                    formControlProps={{
                                        sx: { width: { xs: '100%', md: '-webkit-fill-available' } },
                                    }}
                                    inputLabel={<Typography color='primary'>{_('Tamaño')}</Typography>}
                                    placeholder={_('Tamaño')}
                                    value={weedSize}
                                    onChange={(e) => {
                                        form.setValue('weedSize', e.target.value);
                                    }}
                                    options={selectOptionsMapping.weedSize}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    disabled={areSizesLoading || !form.getValues().weed}
                                />
                            </Stack>
                        )}
                </Stack>
                {form.getValues().weed !== '' && selectedWeed && isWeedSpecificationType(formType) && (
                    <Stack sx={{ mt: 2 }} xs={12}>
                        <Card
                            title={selectedWeed.name}
                            subtitle={selectedWeed.scientificName}
                            rigthIcon={
                                !selectedWeed.images.length ? (
                                    <Icon type='weedRecommendation' />
                                ) : (
                                    <Avatar
                                        src={selectedWeed.images?.[0]?.file || selectedWeed.label}
                                        alt={selectedWeed.label}
                                        sx={{ width: 50, height: 50 }}
                                    />
                                )
                            }
                            placeholder={`${_(selectedWeed.leavesDescription)} ${_(selectedWeed.flowerFruitDescription)} ${_(selectedWeed.rootStemDescription)}`}
                            rightButtonText={_('Saber más')}
                            rightButtonOnClick={handleClick}
                        />
                    </Stack>
                )}
                {form.getValues().crop === 'soy' && form.getValues().type === 'diseases' && (
                    <Stack spacing={1}>
                        <Stack>
                            <Typography color='primary' variant='body2' sx={{ fontWeight: 600 }}>
                                {_('¿El cultivo presenta síntomas?')}
                            </Typography>
                        </Stack>
                        <Stack>
                            <FormControl>
                                <RadioGroup
                                    value={form.watch('applicationType')}
                                    onChange={(e) => form.setValue('applicationType', e.target.value)}
                                >
                                    {hasSymptomChoices.map((sc) => (
                                        <FormControlLabel
                                            key={sc.label}
                                            value={sc.value}
                                            control={
                                                <Radio
                                                    sx={{
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: 20,
                                                        },
                                                    }}
                                                    color='secondary'
                                                />
                                            }
                                            label={<Typography color='primary'>{sc.label}</Typography>}
                                            disabled={!form.watch('diseaseId')}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                    </Stack>
                )}
            </Stack>
            {selectedWeed && <WeedModal weed={selectedWeed} open={open} handleClose={handleClose} />}
        </>
    );
};

export default SpecificationsStep;
